import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import {
    homeOutline,
    calendarOutline,
    settingsOutline,
    personOutline,
    qrCodeOutline,
    bluetoothOutline,
    boatOutline,
    boat,
    home,
    calendar,
    settings,
    person,
} from 'ionicons/icons'
import { useBleStore } from '@/stores/bleStore'
import { useSharedGeolocation } from '@/composables/useSharedGeolocation'

/**
 * Composable for tab navigation business logic
 * Handles:
 * - Tab bar visibility based on current route
 * - BLE proximity detection for scanner icon switching
 * - Geofence-based scanner enablement
 * - Navigation handlers with haptic feedback
 * - Active tab state management
 */
export function useTabNavigation() {
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const bleStore = useBleStore()

    // Geolocation composable - for checking if user is inside geofence
    const { status: locationStatus } = useSharedGeolocation()

    // Reactive current time that updates every second to make BLE check reactive
    const currentTime = ref(Date.now())
    let timeUpdateInterval: ReturnType<typeof setInterval> | null = null

    // Icons for tabs
    const icons = {
        home,
        homeOutline,
        calendar,
        calendarOutline,
        settings,
        settingsOutline,
        person,
        personOutline,
        qrCodeOutline,
        bluetoothOutline,
        boat,
        boatOutline,
    }

    // Hide tab bar on certain routes (ProfileAssignment, AdminPanel, ManualRecordManagement, Scanner, BleCheckin)
    const showTabBar = computed(() => {
        const routeName = route.name as string
        const hiddenRoutes = ['ProfileAssignment', 'AdminPanel', 'ManualRecordManagement', 'Scanner', 'BleCheckin', 'LogsPage']
        return !hiddenRoutes.includes(routeName)
    })

    // Active tab computation
    const activeTab = computed(() => route.name as string)

    // Check if BLE device was seen within last 60 seconds (1 minute)
    const isBleDeviceNearby = computed(() => {
        if (!bleStore.lastSeenTimestamp) return false
        const now = currentTime.value // Use reactive current time
        const oneMinute = 60 * 1000 // 60 seconds in milliseconds
        return (now - bleStore.lastSeenTimestamp) <= oneMinute
    })

    // Scanner/BLE button state - BLE works independently, QR requires geofence
    const isScannerEnabled = computed(() =>
        isBleDeviceNearby.value || locationStatus.value === 'inside'
    )

    // Use Bluetooth icon if BLE device is nearby, otherwise QR icon
    const scannerIcon = computed(() => isBleDeviceNearby.value ? bluetoothOutline : qrCodeOutline)

    const scannerButtonText = computed(() => {
        // BLE takes priority - if device nearby, always show BLE text
        if (isBleDeviceNearby.value) {
            return t('navbar.scanner.bleCheckIn')
        }

        if (locationStatus.value === 'loading') {
            return t('navbar.scanner.checkingLocation')
        }

        if (locationStatus.value !== 'inside') {
            return t('navbar.scanner.locationRequired')
        }

        return t('navbar.scanner.checkIn')
    })

    const scannerButtonClass = computed(() => ({
        'center-fab-button': true,
        'center-fab-button--disabled': !isScannerEnabled.value
    }))

    // Navigation handler with haptic feedback
    async function handleTabClick() {
        try {
            await Haptics.impact({ style: ImpactStyle.Light })
        } catch (error) {
            // Haptic feedback not available on web
        }
    }

    // Scanner/BLE FAB click handler
    async function handleScannerClick() {
        // BLE check-in: allowed when device nearby (no geofence needed)
        // QR check-in: requires geofence validation
        if (isBleDeviceNearby.value) {
            try {
                await Haptics.impact({ style: ImpactStyle.Medium })
            } catch (error) {
                // Haptic feedback not available on web
            }
            router.push({ name: 'BleCheckin' })
            return
        }

        // QR mode - require geofence
        if (locationStatus.value !== 'inside') {
            return
        }

        try {
            await Haptics.impact({ style: ImpactStyle.Medium })
        } catch (error) {
            // Haptic feedback not available on web
        }
        router.push({ name: 'Scanner' })
    }

    // Start interval to update current time every second (makes BLE nearby check reactive)
    onMounted(() => {
        timeUpdateInterval = setInterval(() => {
            currentTime.value = Date.now()
        }, 1000) // Update every second
    })

    // Cleanup on unmount
    onUnmounted(() => {
        if (timeUpdateInterval) {
            clearInterval(timeUpdateInterval)
            timeUpdateInterval = null
        }
    })

    return {
        // State
        showTabBar,
        activeTab,
        isScannerEnabled,
        isBleDeviceNearby,
        locationStatus,

        // Computed
        scannerIcon,
        scannerButtonText,
        scannerButtonClass,

        // Icons
        icons,

        // Handlers
        handleTabClick,
        handleScannerClick
    }
}
